import { motion } from 'framer-motion'
import cn from 'utils/cn'
import MenuItems from 'core/widgets/side-menu/MenuItems'
import Button from 'core/components/buttons/Button'
import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import useTailwindBreakpoint from 'core/hooks/ui/useTailwindBreakpoint'
import usePathSection from 'core/hooks/navigation/usePathSection'
import { useEffect, useMemo } from 'react'
import AccountSwitcher from 'core/widgets/side-menu/AccountSwitcher'
import Icon from 'core/components/icons/Icon'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import useUrlParams from 'core/hooks/navigation/useUrlParams'
import { LINK_HC_HELP_CENTER } from 'constants/Links'

const SideMenu = () => {
  const [open, setOpen] = useLocalStorage('side_menu_open', true)
  const { breakpoint: currentBreakpoint } = useTailwindBreakpoint()
  const { isTeamSpace } = usePathSection()
  const [urlParams] = useUrlParams()
  const inAppView = urlParams.get('inAppView')

  const shouldHide =
    currentBreakpoint === 'xs' ||
    currentBreakpoint === 'sm' ||
    currentBreakpoint === 'md'

  const toggleIcon = useMemo(() => {
    const iconProps = {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      onClick: () => {
        setOpen(!open)
        if (!open) {
          if (!isTeamSpace) {
            AnalyticsHelper.trackEvent('personal_nav_expand')
          }
          if (isTeamSpace) {
            AnalyticsHelper.trackEvent('team_nav_expand')
          }
        }

        if (open) {
          if (!isTeamSpace) {
            AnalyticsHelper.trackEvent('personal_nav_collapse')
          }
          if (isTeamSpace) {
            AnalyticsHelper.trackEvent('team_nav_collapse')
          }
        }
      }
    }

    return open ? (
      <motion.span {...iconProps}>
        <Icon
          name="double-arrow-left"
          className="hover:opacity-80 cursor-pointer "
        />
      </motion.span>
    ) : (
      <motion.span {...iconProps}>
        <Icon
          name="double-arrow-right"
          className="hover:opacity-80 cursor-pointer "
        />
      </motion.span>
    )
  }, [isTeamSpace, open, setOpen])

  useEffect(() => {
    if (shouldHide) setOpen(false)
  }, [setOpen, shouldHide])

  return (
    <motion.aside
      className={cn([
        'side-menu flex flex-col justify-between items-center gap-5 overflow-hidden overflow-y-auto',
        isTeamSpace && 'bg-sidebar-team',
        !isTeamSpace && 'bg-sidebar-personal',
        inAppView && 'hidden',
        open && 'px-3'
      ])}
      initial={false}
      transition={{ ease: 'easeOut' }}
      animate={{
        width: open ? 280 : 65
      }}
      data-testid="side-menu"
    >
      <div className={cn([!open && 'flex flex-col items-center'])}>
        <div className="mb-5 h-20">
          <AccountSwitcher open={open} />
        </div>
        <MenuItems open={open} />
      </div>

      <div className="flex flex-col gap-5 w-full">
        <div
          className={cn([
            'select-none flex pb-3 w-full',
            open ? 'w-full justify-between pr-5' : 'justify-center',
            isTeamSpace ? 'text-white' : 'text-dark-3',
            shouldHide && 'hidden'
          ])}
        >
          {open ? (
            <Button
              variant="link"
              className={cn([isTeamSpace ? 'text-white' : 'text-dark'])}
              onClick={() => {
                window.open(LINK_HC_HELP_CENTER, '_blank')
              }}
            >
              <div className="flex items-center gap-2">
                <Icon
                  name="help-outline"
                  width={20}
                  color={cn([isTeamSpace ? 'text-white' : 'text-dark'])}
                />{' '}
                Help center
              </div>
            </Button>
          ) : null}
          {toggleIcon}
        </div>
      </div>
    </motion.aside>
  )
}

export default SideMenu
